import React, { useState, useEffect } from 'react';
import Grid from 'components/elements/Grid';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import { ContractSearchSelectGroup, ContractTextInput } from '../ContractFields';
import { toMonthInputFormat } from 'contracts/contractUtils';
import { isSafari } from 'shared/Browser';

export const Shipment = ({
  destinationWarehouseIdField,
  destinationWarehouseId,
  setDestinationWarehouseId,
  destinationWarehouseOptions,
  destinationWarehouseHidden,
  warehouseIdField,
  warehouseId,
  setWarehouseId,
  warehouseOptions,
  warehouseHidden,
  shipperPortIdField,
  shipperPortId,
  setShipperPortId,
  destinationPortIdField,
  destinationPortId,
  setDestinationPortId,
  shipmentDateField,
  shipmentDate,
  setShipmentDate,
  shipperPortOptions,
  deliveryDateHidden,
  deliveryDateField,
  deliveryDate,
  setDeliveryDate,
  cancelContractChecked,
  closeContractChecked,
}) => {
  const dateValidation = (dateField) => {
    return dateField && !dateField.readonly && toMonthInputFormat(new Date());
  };

  const [minDeliveryValue, setMinDeliveryValue] = useState(
    (!closeContractChecked &&
      !cancelContractChecked &&
      dateValidation(deliveryDateField)) ||
      undefined
  );
  useEffect(() => {
    setMinDeliveryValue(
      (!closeContractChecked &&
        !cancelContractChecked &&
        dateValidation(deliveryDateField)) ||
        undefined
    );
  }, [closeContractChecked, cancelContractChecked]);

  // PLA-1057: estos date fields usan el date-picker (react-datepicker showMonthYearPicker) en todos
  // los navegadores vía `monthYearPicker`, no el <input type="month"> nativo (que se congelaba al
  // teclear el año y no dejaba elegirlo). El picker entrega/recibe objetos Date; '' = sin fecha.
  const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());

  // PLA-1118: si la fecha de envío pasa el tope de posiciones de futuros, se avisa en rojo bajo el
  // campo apenas se elige (al editar, sólo si se cambia); el backend igual bloquea al guardar.
  const futuresLimit = shipmentDateField?.futuresLimit;
  const selectedShipmentMonth = isValidDate(shipmentDate)
    ? toMonthInputFormat(shipmentDate)
    : '';
  const exceedsFuturesLimit =
    !!futuresLimit &&
    selectedShipmentMonth !== '' &&
    selectedShipmentMonth !== futuresLimit.currentMonth &&
    selectedShipmentMonth > futuresLimit.maxMonth;
  const shipmentDateErrors = [
    ...(shipmentDateField?.errors ?? []),
    ...(exceedsFuturesLimit ? [futuresLimit.message] : []),
  ];

  const changeMinDeliveryValue = (shipment) => {
    if (!isValidDate(shipment)) return setMinDeliveryValue(undefined);
    // Mínimo de la fecha de entrega = mes siguiente al shipment.
    const nextMonth = new Date(shipment.getFullYear(), shipment.getMonth() + 1, 1);
    setMinDeliveryValue(isSafari() ? nextMonth : toMonthInputFormat(nextMonth));
    // PLA-1057: si la fecha de entrega quedó por debajo del nuevo mínimo (antes del mes siguiente al
    // shipment), se ajusta automáticamente a ese mes — como hacía el <input type="month"> nativo.
    if (isValidDate(deliveryDate) && deliveryDate < nextMonth) {
      setDeliveryDate(nextMonth);
    }
  };

  const onChangeDate = (date, setDate) => {
    setDate(date);
    return date;
  };

  return (
    <FieldSetWithTitle title={I18n.translate('contracts.form.shipment')}>
      <Grid>
        {!destinationWarehouseHidden && (
          <ContractSearchSelectGroup
            field={{
              ...destinationWarehouseIdField,
              options: destinationWarehouseIdField?.options?.sort((a, b) =>
                a.name.localeCompare(b.name)
              ),
            }}
            value={destinationWarehouseId}
            onChange={setDestinationWarehouseId}
            overrideOptions={destinationWarehouseOptions}
            required={!destinationWarehouseHidden}
            showAsterisk={false}
          />
        )}
        {!warehouseHidden && (
          <ContractSearchSelectGroup
            field={warehouseIdField}
            value={warehouseId}
            onChange={setWarehouseId}
            overrideOptions={warehouseOptions}
          />
        )}

        <ContractSearchSelectGroup
          overrideOptions={shipperPortOptions}
          field={shipperPortIdField}
          value={shipperPortId}
          onChange={setShipperPortId}
          required
          showAsterisk={false}
        />
        <ContractSearchSelectGroup
          field={destinationPortIdField}
          value={destinationPortId}
          onChange={setDestinationPortId}
          required
          showAsterisk={false}
        />

        <ContractTextInput
          field={shipmentDateField}
          type='month'
          monthYearPicker
          min={
            (!closeContractChecked &&
              !cancelContractChecked &&
              dateValidation(shipmentDateField)) ||
            undefined
          }
          value={shipmentDate}
          errors={shipmentDateErrors}
          onChangeArg={(date) => date ?? ''}
          onChange={(monthDate) => {
            const parsedDate = onChangeDate(monthDate, setShipmentDate);
            if (
              parsedDate &&
              !deliveryDateHidden &&
              !cancelContractChecked &&
              !closeContractChecked
            )
              changeMinDeliveryValue(parsedDate);
          }}
        />
        {!deliveryDateHidden && (
          <ContractTextInput
            field={deliveryDateField}
            min={minDeliveryValue}
            type='month'
            monthYearPicker
            value={deliveryDate}
            onChange={(monthDate) => onChangeDate(monthDate, setDeliveryDate)}
            onChangeArg={(date) => date ?? ''}
          />
        )}
      </Grid>
    </FieldSetWithTitle>
  );
};
